// Decides which chat backend a conversation talks to — the new agentic runtime
// or the legacy querying backend (#8147). The org-level `agentic_chat` flag is
// the only switch. Until #11313 super-admins and prod-impersonating operators
// could override it per page session through a "Query +" button, to preview
// either backend; that affordance is gone now that every organization is moving
// to Agentic Querying.
//
// This module holds the pure decision, plus the one deliberately impure member:
// the fallback-probe reporter (console + Sentry breadcrumb, #11075). It does NOT
// import the concrete backends (which transitively pull in the redux store), so
// it stays cheap to unit test. The trivial instance construction from the
// resolved kind lives at the component seam (ChatConversation), which already
// imports both backends.

import * as Sentry from "@sentry/react";

export type ChatBackendKind = "agentic" | "legacy";

// Narrow view of the redux `userData` this module reads — the store types it as
// `any` app-wide, so we declare just the fields we touch.
export interface ChatBackendUserData {
  organization?: { agentic_chat?: boolean };
}

const orgAgenticChat = (
  userData: ChatBackendUserData | null | undefined
): boolean => Boolean(userData?.organization?.agentic_chat);

// The backend kind a NEW conversation should use. The component turns this into
// an instance. Pure.
export const chatBackendKindForNewConversation = (
  userData: ChatBackendUserData | null | undefined
): ChatBackendKind => (orgAgenticChat(userData) ? "agentic" : "legacy");

// Which backend created an existing conversation, inferred from its id shape:
// agentic ids carry the `pgid-chat-conversations-` prefix; legacy workspace ids
// are bare uuids. Used to tag merged history rows so a click routes to the
// right backend (kept in sync with chatUtils' CHAT_CONVERSATION_ID_PREFIX).
export const chatBackendKindForId = (id: string): ChatBackendKind =>
  id.startsWith("pgid-chat-conversations-") ? "agentic" : "legacy";

// Which universal-sharing entity type a chat id maps to. Agentic conversations
// share through the "conversation" endpoint (grants live in
// chat_conversation_grants); legacy workspace-backed chats through "workspace".
// Every Share entry point (chat header, history sidebar) must agree, or the
// read hits an empty table and — worse — the write 403s against the wrong
// endpoint's auth check.
export const shareEntityTypeForChatId = (
  id: string
): "conversation" | "workspace" =>
  chatBackendKindForId(id) === "agentic" ? "conversation" : "workspace";

// For an EXISTING conversation that loaded with WORKSPACE_NOT_FOUND on the
// resolved backend, the backend kind to retry with — or null when no fallback
// should be attempted (#9955). An existing conversation reached without a
// backend tag (refresh, direct link, share) carries only a bare-uuid slug that
// can't disambiguate the two backends, so the org's setting may resolve the
// wrong one. We probe the other backend once. No probe when the caller pinned a
// backend (Matrix / Notetaker pass an explicit instance), when a nav tag already
// identified the backend (a history click), or when we have already fallen back
// once (bounds the probe so a genuinely-missing conversation can't loop).
//
// Only the agentic->legacy direction probes: it is what lets a user in an
// agentic org still open an old legacy chat by bare uuid. The reverse direction
// existed solely for a "Query +" operator on a flag-off org (#10397) and went
// away with the switcher (#11313), so a legacy 404 is a genuine not-found.
export const chatBackendFallbackKind = (args: {
  resolvedKind: ChatBackendKind;
  hasExplicitBackend: boolean;
  hasNavTag: boolean;
  alreadyProbed: boolean;
}): ChatBackendKind | null => {
  if (args.hasExplicitBackend || args.hasNavTag || args.alreadyProbed) {
    return null;
  }
  return args.resolvedKind === "agentic" ? "legacy" : null;
};

// Compute the fallback for a not-found load and, when one applies, record it.
// The compute and the record are bundled so the trace can't be dropped
// independently of the decision it describes (#11075).
export const resolveChatBackendFallbackProbe = (args: {
  resolvedKind: ChatBackendKind;
  hasExplicitBackend: boolean;
  hasNavTag: boolean;
  alreadyProbed: boolean;
  workspaceSlug: string;
}): ChatBackendKind | null => {
  const fallback = chatBackendFallbackKind(args);
  if (fallback) {
    reportChatBackendFallbackProbe({
      resolvedKind: args.resolvedKind,
      fallbackKind: fallback,
      workspaceId: args.workspaceSlug,
    });
  }
  return fallback;
};

// Record that the not-found fallback probe is switching an existing
// conversation to the other backend (#11075). The switch is silent by design
// — it exists so old legacy chats keep opening for agentic orgs — which is
// exactly why it needs a trace: #11075 (an entry point that created the chat
// in the wrong store and rode this probe onto the wrong engine) had to be
// diagnosed from production data because nothing recorded the probe firing.
const reportChatBackendFallbackProbe = (args: {
  resolvedKind: ChatBackendKind;
  fallbackKind: ChatBackendKind;
  workspaceId: string;
}): void => {
  console.warn(
    `chat backend fallback probe: resolved ${args.resolvedKind} was not found, ` +
      `retrying ${args.workspaceId} as ${args.fallbackKind}`
  );
  Sentry.addBreadcrumb({
    category: "chat",
    message: "chat backend fallback probe",
    level: "warning",
    data: {
      resolvedKind: args.resolvedKind,
      fallbackKind: args.fallbackKind,
      workspaceId: args.workspaceId,
    },
  });
};
